import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Phone, RefreshCw, CheckCircle2, Lock, Package, Truck, Loader2 } from "lucide-react";
import usePageTitle from "@/hooks/usePageTitle";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PIN_KEY = "hk_delivery_pin";

const navigateUrl = (s) => {
  if (s.google_maps_url) return s.google_maps_url;
  if (s.latitude != null && s.longitude != null)
    return `https://www.google.com/maps/dir/?api=1&destination=${s.latitude},${s.longitude}`;
  return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(s.address || "")}`;
};

const isToday = (iso) => {
  if (!iso) return false;
  const d = new Date(iso);
  const now = new Date();
  return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth() && d.getDate() === now.getDate();
};

const STATUS_LABELS = {
  shipment_created: "Label created",
  picked_up: "Picked up",
  in_transit: "In transit",
  out_for_delivery: "Out for delivery",
};

const ShipmentCard = ({ s, onDelivered, marking }) => (
  <div className="bg-white rounded-xl border border-[var(--hk-border)] p-4 space-y-3 shadow-sm" data-testid={`delivery-card-${s.id}`}>
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <p className="font-semibold text-[15px]">{s.customer_name}</p>
        <p className="text-[13px] text-[var(--hk-ink-2)] mt-0.5 leading-snug">{s.address}</p>
      </div>
      <span className="shrink-0 text-[10px] px-2 py-1 rounded-full bg-[var(--hk-warm-ivory)] border border-[var(--hk-border)] uppercase tracking-wider">
        {STATUS_LABELS[s.status] || (s.status || "").replace(/_/g, " ")}
      </span>
    </div>
    <p className="text-[12px] text-[var(--hk-ink-3)]">
      {(s.items || []).map((i) => `${i.name} ×${i.qty}`).join(", ")} · ₹{((s.total || 0) / 100).toLocaleString("en-IN")} ({s.payment_status === "paid" ? "Prepaid" : s.payment_status})
      {s.awb && <> · AWB <span className="font-mono">{s.awb}</span></>}
    </p>
    <div className="grid grid-cols-2 gap-2">
      <a href={navigateUrl(s)} target="_blank" rel="noreferrer"
        className="flex items-center justify-center gap-2 h-12 rounded-lg bg-[var(--hk-orange-deep)] text-white font-semibold text-[14px] active:scale-[0.98] transition-transform"
        data-testid={`delivery-navigate-${s.id}`}>
        <MapPin size={16} /> Navigate
      </a>
      <a href={`tel:${(s.phone || "").replace(/\s/g, "")}`}
        className="flex items-center justify-center gap-2 h-12 rounded-lg border border-[var(--hk-border)] font-semibold text-[14px] active:scale-[0.98] transition-transform"
        data-testid={`delivery-call-${s.id}`}>
        <Phone size={16} /> Call
      </a>
    </div>
    <button
      onClick={() => onDelivered(s.id)}
      disabled={marking === s.id}
      className="w-full h-11 rounded-lg border border-emerald-300 text-emerald-700 bg-emerald-50 font-medium text-[13.5px] flex items-center justify-center gap-2 active:scale-[0.98] transition-transform"
      data-testid={`delivery-delivered-${s.id}`}
    >
      {marking === s.id ? <Loader2 size={15} className="animate-spin" /> : <CheckCircle2 size={15} />} Mark Delivered
    </button>
  </div>
);

export default function DeliveryPage() {
  usePageTitle("Delivery Staff");
  const [pin, setPin] = useState(sessionStorage.getItem(PIN_KEY) || "");
  const [authed, setAuthed] = useState(false);
  const [pinError, setPinError] = useState("");
  const [unlocking, setUnlocking] = useState(false);
  const [shipments, setShipments] = useState(null);
  const [marking, setMarking] = useState(null);
  const [enabled, setEnabled] = useState(true);

  useEffect(() => {
    axios.get(`${API}/delivery/status`)
      .then((r) => setEnabled(!!r.data?.enabled))
      .catch(() => setEnabled(true)); // fail-open: let the PIN check decide
  }, []);

  const load = useCallback(async (p) => {
    const r = await axios.get(`${API}/delivery/shipments`, { headers: { "X-Delivery-Pin": p } });
    setShipments(r.data || []);
  }, []);

  useEffect(() => {
    if (!pin) return;
    load(pin).then(() => setAuthed(true)).catch(() => sessionStorage.removeItem(PIN_KEY));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const unlock = async () => {
    setUnlocking(true);
    setPinError("");
    try {
      await axios.post(`${API}/delivery/verify`, {}, { headers: { "X-Delivery-Pin": pin } });
      sessionStorage.setItem(PIN_KEY, pin);
      setAuthed(true);
      await load(pin);
    } catch (e) {
      const status = e?.response?.status;
      setPinError(status === 403
        ? "Delivery access is not enabled yet. Ask the admin to set a PIN under Admin › Logistics."
        : "Wrong PIN. Try again.");
    } finally {
      setUnlocking(false);
    }
  };

  const refresh = async () => {
    try {
      await load(pin);
      toast.success("Refreshed");
    } catch {
      toast.error("Refresh failed");
    }
  };

  const markDelivered = async (id) => {
    setMarking(id);
    try {
      await axios.post(`${API}/delivery/orders/${id}/delivered`, {}, { headers: { "X-Delivery-Pin": pin } });
      setShipments((list) => (list || []).filter((s) => s.id !== id));
      toast.success("Marked delivered");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update");
    } finally {
      setMarking(null);
    }
  };

  if (!authed) {
    return (
      <div className="min-h-screen bg-[var(--hk-cream)] flex items-center justify-center p-5">
        <div className="w-full max-w-sm bg-white rounded-2xl border border-[var(--hk-border)] p-6 space-y-4 shadow-sm" data-testid="delivery-lock">
          <div className="flex items-center gap-2.5">
            <Truck size={20} className="text-[var(--hk-orange-deep)]" />
            <div>
              <h1 className="text-lg font-semibold">HerbKranti Delivery</h1>
              <p className="text-[12px] text-[var(--hk-ink-3)]">Today's runs — staff only</p>
            </div>
          </div>
          <label className="block">
            <span className="text-[11px] uppercase tracking-widest text-[var(--hk-ink-2)]">Staff PIN</span>
            <input
              type="password" inputMode="numeric" value={pin}
              onChange={(e) => setPin(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && unlock()}
              className="mt-1.5 w-full px-3 py-3 rounded-lg border border-[var(--hk-border)] text-[16px] tracking-[0.3em]"
              placeholder="••••" data-testid="delivery-pin-input"
            />
          </label>
          {!enabled && (
            <p className="text-[12.5px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3" data-testid="delivery-disabled-notice">
              Delivery access is not enabled yet. Ask the admin to set a PIN under Admin › Logistics.
            </p>
          )}
          {pinError && <p className="text-[12.5px] text-red-600" data-testid="delivery-pin-error">{pinError}</p>}
          <button onClick={unlock} disabled={unlocking || !pin.trim()}
            className="w-full h-12 rounded-lg bg-[var(--hk-orange-deep)] text-white font-semibold flex items-center justify-center gap-2 active:scale-[0.98] transition-transform disabled:opacity-50"
            data-testid="delivery-unlock-btn">
            {unlocking ? <Loader2 size={16} className="animate-spin" /> : <Lock size={15} />} Unlock
          </button>
        </div>
      </div>
    );
  }

  const today = (shipments || []).filter((s) => isToday(s.shipped_at));
  const earlier = (shipments || []).filter((s) => !isToday(s.shipped_at));

  return (
    <div className="min-h-screen bg-[var(--hk-cream)] pb-10" data-testid="delivery-page">
      <div className="sticky top-0 z-10 bg-[var(--hk-cream)]/95 backdrop-blur border-b border-[var(--hk-border)] px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Truck size={18} className="text-[var(--hk-orange-deep)]" />
          <h1 className="font-semibold text-[15px]">Deliveries ({(shipments || []).length})</h1>
        </div>
        <button onClick={refresh} className="h-10 w-10 rounded-lg border border-[var(--hk-border)] bg-white flex items-center justify-center active:scale-95 transition-transform" data-testid="delivery-refresh-btn">
          <RefreshCw size={15} />
        </button>
      </div>

      <div className="px-4 pt-4 space-y-5 max-w-md mx-auto">
        {shipments === null ? (
          <p className="text-center text-[var(--hk-ink-2)] py-10">Loading…</p>
        ) : shipments.length === 0 ? (
          <div className="text-center py-16 space-y-2" data-testid="delivery-empty">
            <Package size={32} className="mx-auto text-[var(--hk-ink-3)]" />
            <p className="text-[14px] text-[var(--hk-ink-2)]">All caught up — no pending deliveries.</p>
          </div>
        ) : (
          <>
            <div className="space-y-3">
              <p className="text-[11px] uppercase tracking-widest text-[var(--hk-ink-2)]">Today ({today.length})</p>
              {today.length === 0 && <p className="text-[12.5px] text-[var(--hk-ink-3)]">Nothing new shipped today.</p>}
              {today.map((s) => <ShipmentCard key={s.id} s={s} onDelivered={markDelivered} marking={marking} />)}
            </div>
            {earlier.length > 0 && (
              <div className="space-y-3">
                <p className="text-[11px] uppercase tracking-widest text-[var(--hk-ink-2)]">Earlier ({earlier.length})</p>
                {earlier.map((s) => <ShipmentCard key={s.id} s={s} onDelivered={markDelivered} marking={marking} />)}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
